import React, { useState } from 'react';
import { cn } from '../utils/cn';
import { ItemIcon } from './ItemIcon';

interface TextComponent {
  text: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underlined?: boolean;
  strikethrough?: boolean;
  obfuscated?: boolean;
  clickEvent?: {
    action: 'open_url' | 'run_command' | 'suggest_command' | 'copy_to_clipboard';
    value: string;
  };
  hoverEvent?: {
    action: 'show_text' | 'show_item' | 'show_entity';
    contents: any;
  };
  extra?: TextComponent[];
}

const colorMap: Record<string, string> = {
  black: 'text-black', dark_blue: 'text-blue-900', dark_green: 'text-green-900',
  dark_aqua: 'text-cyan-900', dark_red: 'text-red-900', dark_purple: 'text-purple-900',
  gold: 'text-yellow-600', gray: 'text-gray-500', dark_gray: 'text-gray-700',
  blue: 'text-blue-500', green: 'text-green-500', aqua: 'text-cyan-500',
  red: 'text-red-500', light_purple: 'text-purple-500', yellow: 'text-yellow-500',
  white: 'text-white',
};

const Tooltip: React.FC<{ content: React.ReactNode; children: React.ReactNode }> = ({ content, children }) => (
  <div className="relative inline-block group">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-xs px-2.5 py-2 bg-dark-900 border border-glass-200/15 rounded-lg text-xs text-white shadow-popover opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
      {content}
    </div>
  </div>
);

const MinecraftText: React.FC<{
  component: TextComponent | string | TextComponent[];
  inheritedStyle?: Partial<TextComponent>;
  clusterId?: string;
}> = ({ component, inheritedStyle = {}, clusterId = 'common' }) => {
  
  if (Array.isArray(component)) {
    return <>{component.map((part, i) => <MinecraftText key={i} component={part} inheritedStyle={inheritedStyle} clusterId={clusterId} />)}</>;
  }
  if (typeof component === 'string') {
    return <span>{component}</span>;
  }

  const style = { ...inheritedStyle, ...component };
  const { text, color, bold, italic, underlined, strikethrough, obfuscated, extra, clickEvent, hoverEvent } = style;
  const [copied, setCopied] = useState(false);

  const classes = cn(
    color && colorMap[color],
    { 'font-bold': bold, 'italic': italic, 'underline': underlined, 'line-through': strikethrough },
    clickEvent && 'cursor-pointer hover:brightness-125'
  );

  const inlineStyle: React.CSSProperties = {};
  if (color && !colorMap[color]) {
    inlineStyle.color = color;
  }

  const handleClick = () => {
    if (!clickEvent) return;
    switch (clickEvent.action) {
      case 'open_url':
        window.open(clickEvent.value, '_blank');
        break;
      case 'copy_to_clipboard':
      case 'suggest_command':
      case 'run_command':
        navigator.clipboard.writeText(clickEvent.value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        break;
    }
  };

  const renderHoverContent = () => {
    if (!hoverEvent) return null;
    switch (hoverEvent.action) {
      case 'show_text':
        return <MinecraftText component={hoverEvent.contents} clusterId={clusterId} />;
      case 'show_item': {
        let itemId = '';
        if (typeof hoverEvent.contents === 'string') {
          itemId = hoverEvent.contents;
        } else if (hoverEvent.contents.id) {
          itemId = hoverEvent.contents.id;
        }
        return itemId ? <ItemIcon itemId={itemId} clusterId={clusterId} displayMode="inline" /> : null;
      }
      default:
        return null;
    }
  };

  const mainContent = (
    <span className={classes} style={inlineStyle} onClick={handleClick}>
      {obfuscated ? '█████' : text}
      {extra && <MinecraftText component={extra} inheritedStyle={style} clusterId={clusterId} />}
    </span>
  );
  
  if (copied) {
    return <Tooltip content="Copied!">{mainContent}</Tooltip>;
  }
  
  const hoverContent = renderHoverContent();
  if (hoverContent) {
    return <Tooltip content={hoverContent}>{mainContent}</Tooltip>;
  }

  return mainContent;
};

export default MinecraftText;
